import { Add, ArrowPointLeft, ArrowPointRight, WidgetSettings } from "assets/icons/Common";
import { DashboardContext, INITIAL_REMEMBER_SETTINGS } from "contexts/DashboardContext";
import { LOCALSTORAGE_WIDGET_KEY } from "hooks/widgets";
import { useContext, useEffect, useState } from "react";
import { OverlayTrigger } from "react-bootstrap";
import { TYPE_ACTION } from "types/dashboard";
import { DashboardProps, GlobalData } from "types/GlobalData";
import { findDashboardGroupById, sanitizeDashboardGroups } from "util/dashboardGroups";
import { loadData as wpLoadData, saveData, forceSaveData } from "util/wp-api";
import { v4 as uuidv4 } from "uuid";

import { DashboardSettings } from "./DashboardSettings";
import {
  AddBtn,
  LeftArrowBox,
  OuterContainer,
  Pagination,
  RightArrowBox,
  SettingsBtn,
  TooltipStyled,
  VerticalLine,
} from "./multipleDashboards.styles";
import { getRandomBackground } from "./utils";

export interface PaginationProps {
  activeIndex: number;
}

const renderTooltip = (dashboards: DashboardProps[], index: number, rest: any) => {
  return <TooltipStyled {...rest}>{dashboards[index]?.name}</TooltipStyled>;
};

interface MultipleDashboardsBoxProps {
  activeIndex: number;
  onSetDashboardData: (id: string, data: DashboardProps) => void;
}

export const MultipleDashboardsBox = (props: MultipleDashboardsBoxProps) => {
  const { activeIndex, onSetDashboardData: onSetActiveDashboard } = props;
  const { data: globalData, setData: updateGlobalData } = useContext(DashboardContext);

  const validGroups = sanitizeDashboardGroups(globalData.dashboardGroups);
  const activeGroup = validGroups.find((group) => group.id === globalData.activeDashboardGroupId);
  const persistedActiveGroup = findDashboardGroupById(
    globalData.dashboardGroups,
    globalData.activeDashboardGroupId,
  );
  const dashboards = activeGroup?.dashboards ?? [];

  const [isSettingsOpen, setSettingsOpen] = useState<boolean>(false);

  if (!dashboards || dashboards.length === 0) {
    return <div style={{ display: "none" }}></div>; // Return hidden div instead of null to satisfy JSX requirements
  }

  // Function to clean up orphaned widgets that don't belong to any existing dashboard
  const cleanupOrphanedWidgets = async () => {
    try {
      const widgetData = await wpLoadData(LOCALSTORAGE_WIDGET_KEY);
      if (!widgetData) return;

      const allDashboardIds = new Set<string>();

      // Collect all dashboard IDs from all groups
      validGroups.forEach((group) => {
        group.dashboards.forEach((dashboard) => {
          allDashboardIds.add(dashboard.id);
        });
      });

      // Find orphaned widgets (widgets whose dashboard ID doesn't exist in dashboard groups)
      const orphanedWidgetIds = Object.keys(widgetData).filter((widgetId) => {
        const dashboardId = widgetId.split("|")[1];
        return dashboardId && !allDashboardIds.has(dashboardId);
      });

      if (orphanedWidgetIds.length > 0) {
        console.log(`Found ${orphanedWidgetIds.length} orphaned widgets, cleaning up...`);

        // Remove orphaned widgets
        const cleanedWidgetData = { ...widgetData };
        orphanedWidgetIds.forEach((id) => {
          delete cleanedWidgetData[id];
        });

        // Save cleaned widget data
        await saveData(LOCALSTORAGE_WIDGET_KEY, cleanedWidgetData);
      }
    } catch (error) {
      console.error("Error cleaning up orphaned widgets:", error);
    }
  };

  const handleCreateNewDashboard = async () => {
    const newDashboardId = uuidv4();
    const newDashboard: DashboardProps = {
      id: newDashboardId,
      name: "New Slide",
      backgroundName: getRandomBackground(dashboards),
      fontName: "LEMON MILK Pro, Arial, sans-serif",
      rememberSettings: INITIAL_REMEMBER_SETTINGS,
      voiceType: "Female",
    };

    // Find the active dashboard group
    const activeGroupIndex = (globalData.dashboardGroups ?? []).findIndex(
      (group) => group?.id === globalData.activeDashboardGroupId,
    );

    let newGlobalData: Partial<GlobalData>;

    if (activeGroupIndex >= 0) {
      // Update the existing active dashboard group
      const updatedDashboardGroups = [...globalData.dashboardGroups];
      const existingDashboards = updatedDashboardGroups[activeGroupIndex].dashboards;
      const updatedDashboards = [
        ...(Array.isArray(existingDashboards) ? existingDashboards : []),
        newDashboard,
      ];

      updatedDashboardGroups[activeGroupIndex] = {
        ...updatedDashboardGroups[activeGroupIndex],
        dashboards: updatedDashboards,
      };

      newGlobalData = {
        dashboardGroups: updatedDashboardGroups,
        activeDashboardGroupId: globalData.activeDashboardGroupId, // Ensure active group ID is preserved
        activeDashboardId: newDashboardId,
      };
    } else {
      // If the active dashboard group is not found, create a new one
      const newGroupId = uuidv4(); // Generate a new group ID
      const newGroup = {
        id: newGroupId,
        name: "New Group",
        dashboards: [newDashboard],
      };

      newGlobalData = {
        dashboardGroups: [...(globalData.dashboardGroups ?? []), newGroup],
        activeDashboardGroupId: newGroupId, // Set the new group as active
        activeDashboardId: newDashboardId,
      };
    }

    // Update local state first
    updateGlobalData(newGlobalData);

    // Clean up orphaned widgets to ensure synchronization
    await cleanupOrphanedWidgets();

    // Force a small delay to ensure the component re-renders with updated dashboard count
    // This ensures the pagination shows the correct count immediately
    setTimeout(async () => {
      // Trigger a re-render by updating the active dashboard to itself
      // This ensures the pagination updates correctly
      onSetActiveDashboard(newDashboardId, newDashboard);

      // Force immediate save of widget data to ensure it's captured with the new dashboard
      // This ensures that when widgets are added to the new slide, they are properly saved
      console.log("Forcing immediate save after new dashboard creation");

      try {
        // Force save the complete current global data to ensure active dashboard state is preserved
        // Use the updated global data that includes the new active dashboard
        await forceSaveData("DashboardData", {
          ...globalData,
          ...newGlobalData,
        });
        console.log("Complete dashboard data force saved successfully with dashboard groups");
      } catch (error) {
        console.error("Failed to force save dashboard data:", error);
      }
    }, 0);
  };

  const handlePrevDashboard = async () => {
    const prevDashboard = dashboards[activeIndex - 1];

    if (prevDashboard) {
      // First, ensure current dashboard state is synchronized with WordPress
      try {
        console.log("Synchronizing current dashboard state before switching to previous dashboard");
        await forceSaveData("DashboardData", globalData);
        console.log("Current dashboard state synchronized successfully");
      } catch (error) {
        console.error("Failed to synchronize current dashboard state:", error);
      }

      // Then switch to the previous dashboard
      onSetActiveDashboard(prevDashboard.id, prevDashboard);

      // Also update the global state directly to ensure activeDashboardId is set correctly
      updateGlobalData({
        activeDashboardId: prevDashboard.id,
      });

      // Force save the updated global state with the new activeDashboardId to WordPress
      try {
        console.log("Saving updated activeDashboardId to WordPress:", prevDashboard.id);
        await forceSaveData("DashboardData", {
          ...globalData,
          activeDashboardId: prevDashboard.id,
        });
        console.log("Active dashboard ID synchronized with WordPress successfully");
      } catch (error) {
        console.error("Failed to synchronize activeDashboardId with WordPress:", error);
      }

      console.log("Switched to previous dashboard:", prevDashboard.id);
    }
  };

  const handleNextDashboard = async () => {
    const nextDashboard = dashboards[activeIndex + 1];

    if (nextDashboard) {
      // First, ensure current dashboard state is synchronized with WordPress
      try {
        console.log("Synchronizing current dashboard state before switching to next dashboard");
        await forceSaveData("DashboardData", globalData);
        console.log("Current dashboard state synchronized successfully");
      } catch (error) {
        console.error("Failed to synchronize current dashboard state:", error);
      }

      // Then switch to the next dashboard
      onSetActiveDashboard(nextDashboard.id, nextDashboard);

      // Also update the global state directly to ensure activeDashboardId is set correctly
      updateGlobalData({
        activeDashboardId: nextDashboard.id,
      });

      // Force save the updated global state with the new activeDashboardId to WordPress
      try {
        console.log("Saving updated activeDashboardId to WordPress:", nextDashboard.id);
        await forceSaveData("DashboardData", {
          ...globalData,
          activeDashboardId: nextDashboard.id,
        });
        console.log("Active dashboard ID synchronized with WordPress successfully");
      } catch (error) {
        console.error("Failed to synchronize activeDashboardId with WordPress:", error);
      }

      console.log("Switched to next dashboard:", nextDashboard.id);
    }
  };

  const handleCloseSettings = async (data: DashboardProps, typeAction?: TYPE_ACTION) => {
    setSettingsOpen(false);

    if (!typeAction) {
      return;
    }

    let newData: DashboardProps[] = [];

    if (typeAction === TYPE_ACTION.update) {
      onSetActiveDashboard(data.id, data);
    }

    if (typeAction === TYPE_ACTION.delete) {
      const persistedDashboards = persistedActiveGroup?.dashboards;
      newData = (Array.isArray(persistedDashboards) ? persistedDashboards : []).filter(
        (dashboard) => dashboard?.id !== data.id,
      );

      if (!activeIndex) {
        // if you remove the first dashboard
        const nextDashboard = dashboards[activeIndex + 1];
        const newDashboardGroups = globalData.dashboardGroups.map((group) => {
          return group?.id === globalData.activeDashboardGroupId
            ? {
                ...group,
                dashboards: newData,
              }
            : group;
        });

        updateGlobalData({
          dashboardGroups: newDashboardGroups,
          activeDashboardId: nextDashboard.id,
        });

        // Force immediate save to ensure dashboard deletion is synchronized with localStorage
        try {
          await forceSaveData("DashboardData", {
            ...globalData,
            dashboardGroups: newDashboardGroups,
            activeDashboardId: nextDashboard.id,
          });
          console.log("Dashboard deletion synchronized with localStorage successfully");
        } catch (error) {
          console.error("Failed to synchronize dashboard deletion:", error);
        }
      } else {
        const prevDashboard = dashboards[activeIndex - 1];
        const newDashboardGroups = globalData.dashboardGroups.map((group) => {
          return group?.id === globalData.activeDashboardGroupId
            ? {
                ...group,
                dashboards: newData,
              }
            : group;
        });

        updateGlobalData({
          dashboardGroups: newDashboardGroups,
          activeDashboardId: prevDashboard.id,
        });

        // Force immediate save to ensure dashboard deletion is synchronized with localStorage
        try {
          await forceSaveData("DashboardData", {
            ...globalData,
            dashboardGroups: newDashboardGroups,
            activeDashboardId: prevDashboard.id,
          });
          console.log("Dashboard deletion synchronized with localStorage successfully");
        } catch (error) {
          console.error("Failed to synchronize dashboard deletion:", error);
        }
      }
    }
  };

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  return (
    <>
      <OverlayTrigger
        placement="top"
        delay={{ show: 200, hide: 300 }}
        overlay={(tooltipProps) => renderTooltip(dashboards, activeIndex, tooltipProps)}
      >
        <OuterContainer>
          <LeftArrowBox
            onClick={handlePrevDashboard}
            className="left-arrow-nav"
            disabled={activeIndex === 0}
          >
            <ArrowPointLeft />
          </LeftArrowBox>

          <Pagination className="swiper-custom-pagination">
            {`${activeIndex + 1}/${dashboards.length}`}
          </Pagination>

          <RightArrowBox
            onClick={handleNextDashboard}
            className="right-arrow-nav"
            disabled={activeIndex + 1 === dashboards.length}
          >
            <ArrowPointRight />
          </RightArrowBox>

          <VerticalLine />

          <AddBtn onClick={handleCreateNewDashboard} disabled={dashboards.length === 10}>
            <Add />
          </AddBtn>

          <VerticalLine />

          <SettingsBtn onClick={handleOpenSettings}>
            <WidgetSettings />
          </SettingsBtn>
        </OuterContainer>
      </OverlayTrigger>

      <DashboardSettings
        textPagination={` ${activeIndex + 1}/${dashboards.length}`}
        isOpen={isSettingsOpen}
        onClose={handleCloseSettings}
        hasDeleteBtn={dashboards.length > 1}
        currentIndex={activeIndex}
      />
    </>
  );
};
